import { useEffect, useRef, useState } from 'react';
import { Button, Paper, Typography, Container, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AudioDevice from './AudioDevice/AudioDevice';
import ProgressBar from './ProgressBar/ProgressBar';
import useAudioTest from './useAudioTest/useAudioTest';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import Microphone from '../../../icons/Microphone';
import Speaker from '../../../icons/SpeakerIcon';
import { PrimaryButton } from '../../common/PrimaryButton';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  paper: {
    display: 'inline-block',
    padding: '23px',
    borderRadius: '8px',
    minHeight: '280px',
    width: '388px',
    '@media screen and (max-width: 400px)': {
      width: '90vw',
      paddingBottom: '10px',
    },
  },
  audioLevelContainer: {
    display: 'flex',
    alignItems: 'center',
    height: '2.5em',
  },
  topLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media screen and (max-width: 950px)': {
      display: 'flex',
      justifyContent: 'center',
    },
    '@media screen and (max-width: 300px)': {
      height: 'calc(100vh - 100px)',
      overflow: 'auto',
      paddingTop: '50px',
      paddingBottom: '5px',
    },
  },
  mainGridContainer: {
    maxWidth: '95%',
    '@media screen and (max-width: 950px)': {
      paddingTop: '10px',
    },
  },
  primaryButtons: {
    '@media screen and (max-width: 300px)': {
      paddingLeft: '50px',
      height: '80px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
  },
});

export function AudioTest() {
  const classes = useStyles();
  const [inputDeviceId, setInputDeviceId] = useState('');
  const [outputDeviceId, setOutputDeviceId] = useState('');
  const previousInputDeviceIdRef = useRef('');
  const { state, dispatch } = useAppStateContext();
  const { t } = useTranslation('', { keyPrefix: 'MICROPHONE' });

  const {
    error,
    setError,
    isRecording,
    isAudioInputTestRunning,
    isAudioOutputTestRunning,
    playAudio,
    playbackURI,
    readAudioInput,
    stopAudioTest,
    inputLevel,
    outputLevel,
  } = useAudioTest();

  const volumeLevel = isAudioOutputTestRunning ? outputLevel : inputLevel;

  const disableAll = isRecording || isAudioOutputTestRunning || (!!error && error !== 'No audio detected');

  const handleRecordClick = () => {
    readAudioInput({ deviceId: inputDeviceId, enableRecording: true });
  };

  const handlePlayClick = () => {
    playAudio({ deviceId: outputDeviceId, testURI: playbackURI });
  };

  // stop test when not on AudioTest and there's an active test
  useEffect(() => {
    if (state.activePane !== ActivePane.AudioTest && (isAudioOutputTestRunning || isAudioInputTestRunning)) {
      stopAudioTest();
    }
  }, [state.activePane, stopAudioTest, isAudioInputTestRunning, isAudioOutputTestRunning]);

  // start audio test when on AudioTest and deviceId changes
  useEffect(() => {
    if (state.activePane === ActivePane.AudioTest) {
      const newInputDeviceSelected = previousInputDeviceIdRef.current !== inputDeviceId;
      previousInputDeviceIdRef.current = inputDeviceId;

      // Restarts the test to continuously capture audio input
      if (!error && (newInputDeviceSelected || (!isRecording && !isAudioInputTestRunning))) {
        readAudioInput({ deviceId: inputDeviceId });
      }
    }
    if (error) {
      stopAudioTest();
    }
  }, [
    error,
    state.activePane,
    inputDeviceId,
    isRecording,
    isAudioInputTestRunning,
    readAudioInput,
    dispatch,
    stopAudioTest,
  ]);

  function setDeviceTestIsFinished() {
    dispatch({ type: 'next-pane' });
    dispatch({ type: 'set-device-test-not-finished', state: false });
  }

  return (
    <Container>
      <Grid container className={classes.gridContainer}>
        <Grid item md={5}>
          <Typography variant="h1" gutterBottom>
            {t('TEST_YOUR_MICROPHONE')}
          </Typography>

          <Typography variant="body1" gutterBottom>
            {t('RECORD_AN_AUDIO_CLIP')}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong> {t('DOES_YOUR_AUDIO_SOUND_GOOD')}</strong>
          </Typography>

          <PrimaryButton
            style={{ marginRight: '1.5em', borderRadius: '6px' }}
            color="primary"
            onClick={setDeviceTestIsFinished}
            disabled={!!error && error !== 'No audio detected'}
          >
            {t('YES')}
          </PrimaryButton>

          <Button
            color="primary"
            disableRipple
            onClick={setDeviceTestIsFinished}
            disabled={!!error && error !== 'No audio detected'}
          >
            {t('SKIP_FOR_NOW')}
          </Button>
        </Grid>
        ​
        <Grid item md={5} className={classes.mainGridContainer}>
          <Paper className={classes.paper}>
            <div className={classes.topLine}>
              <Typography variant="subtitle2">
                <strong>{t('AUDIO')}</strong>
              </Typography>
              <div className={classes.primaryButtons}>
                <PrimaryButton disabled={disableAll} onClick={handleRecordClick}>
                  {t('RECORD')}
                </PrimaryButton>
                <PrimaryButton disabled={!playbackURI || disableAll} onClick={handlePlayClick}>
                  {t('PLAY_BACK')}
                </PrimaryButton>
              </div>
            </div>
            ​
            <AudioDevice
              disabled={disableAll}
              kind="audiooutput"
              onDeviceChange={setOutputDeviceId}
              setDeviceError={setError}
            />
            <AudioDevice
              disabled={disableAll}
              kind="audioinput"
              onDeviceChange={setInputDeviceId}
              setDeviceError={setError}
              error={error}
            />
            <div className={classes.audioLevelContainer}>
              <div style={{ width: '2em', display: 'flex', justifyContent: 'center' }}>
                {isAudioOutputTestRunning ? <Speaker /> : <Microphone />}
              </div>
              <ProgressBar position={volumeLevel} duration={0.1} style={{ flex: '1' }} />
            </div>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
